import { forwardRef, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { MediaNotFound } from '@/components/Message/MediaNotFound';

interface ResultVideoProps {
  url?: string;
  startTime?: number;
  endTime?: number;
  containerClassName?: string;
  videoClassName?: string;
  notFoundText?: string;
}

export const ResultVideo = forwardRef<HTMLVideoElement, ResultVideoProps>(
  (
    {
      url,
      startTime,
      endTime,
      containerClassName,
      videoClassName,
      notFoundText,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const internalRef = useRef<HTMLVideoElement>(null);
    const videoRef = ref || internalRef;

    // Adjust startTime if it equals endTime
    const adjustedStartTime =
      startTime !== undefined && endTime !== undefined && startTime === endTime
        ? Math.max(0, startTime - 1)
        : startTime;

    const setVideoStartTime = () => {
      if (
        videoRef &&
        typeof videoRef !== 'function' &&
        videoRef.current &&
        adjustedStartTime !== undefined
      ) {
        videoRef.current.currentTime = adjustedStartTime;
      }
    };

    const setVideoEndTime = () => {
      if (
        videoRef &&
        typeof videoRef !== 'function' &&
        videoRef.current &&
        endTime !== undefined
      ) {
        videoRef.current.currentTime = endTime;
      }
    };

    const handleTimeUpdate = () => {
      if (
        videoRef &&
        typeof videoRef !== 'function' &&
        videoRef.current &&
        adjustedStartTime !== undefined &&
        endTime !== undefined
      ) {
        // Prevent playing before startTime
        if (videoRef.current.currentTime < adjustedStartTime) {
          videoRef.current.currentTime = adjustedStartTime;
        }
        // Pause at endTime
        if (videoRef.current.currentTime >= endTime) {
          videoRef.current.pause();
        }
      }
    };

    const handlePlay = () => {
      if (
        videoRef &&
        typeof videoRef !== 'function' &&
        videoRef.current &&
        adjustedStartTime !== undefined &&
        endTime !== undefined
      ) {
        if (videoRef.current.currentTime >= endTime) {
          videoRef.current.currentTime = adjustedStartTime;
        }
      }
    };

    const renderErrorElement = (text: string) => {
      return (
        <div className="flex items-center gap-1.5">
          <i className="ki-outline ki-information-1 text-purple-400 text-xl" />
          <div className="text-white font-medium">{text}</div>
        </div>
      );
    };

    return (
      <div className={twMerge('relative w-[560px] h-full', containerClassName)}>
        {url ? (
          <video
            ref={videoRef}
            crossOrigin="anonymous"
            controls
            controlsList="nodownload nopictureinpicture"
            disablePictureInPicture
            preload="metadata"
            className={twMerge('w-full h-full', videoClassName)}
            onLoadedMetadata={() => setVideoStartTime()}
            onPlay={handlePlay}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => setVideoEndTime()}
          >
            <source src={url} type="video/mp4" />
            {renderErrorElement(t('common:browserNotCompatibleWithTheVideo'))}
          </video>
        ) : (
          <MediaNotFound
            defaultIconClassName="ki-youtube"
            notFoundText={notFoundText}
          />
        )}
      </div>
    );
  }
);
